import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactEcharts from 'echarts-for-react';
import * as ExcelJS from 'exceljs';
import { useAuth } from '../../../components/auth/AuthContext';
import CommodityTradesGrid from '../../../components/grids/CommodityTradesGrid';
import { BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import { clientService as clientApiService, TradingEntity } from '../../../services/api/clientService';
import { clientService } from '../../../services/clientService';
import { logger } from '../../../utils/logger';
import { useSystemDate } from '../../../hooks/useSystemDate';
import '../../client/Positions.css';

const fmtDate = (v: any): string => {
  if (!v || typeof v !== 'string') return '';
  const parts = v.split('-');
  if (parts.length !== 3) return v;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const parseDate = (v: any): Date | null => {
  if (!v || typeof v !== 'string') return null;
  const parts = v.split('-');
  if (parts.length !== 3) return null;
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
};

const formatAmount = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue >= 1000000000) return (value / 1000000000).toFixed(2) + 'B';
  if (absValue >= 1000000) return (value / 1000000).toFixed(2) + 'M';
  if (absValue >= 1000) return (value / 1000).toFixed(1) + 'K';
  return value.toFixed(0);
};

interface TradeBar {
  label: string;
  value: number;
  underlying: string;
  direction: string;
  isTotal: boolean;
}

const CommodityPositions: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || user?.id;

  const [trades, setTrades] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [entities, setEntities] = useState<TradingEntity[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<string>('all');
  const [selectedTrades, setSelectedTrades] = useState<any[]>([]);
  const [selectAllTrigger, setSelectAllTrigger] = useState(0);
  const [clearAllTrigger, setClearAllTrigger] = useState(0);
  const [tradeNumberSearch, setTradeNumberSearch] = useState('');
  const [filterActiveOnly, setFilterActiveOnly] = useState(false);
  const [chartExpanded, setChartExpanded] = useState(false);
  const systemDateResult = useSystemDate(clientId);
  const maturityDateFrom = filterActiveOnly ? systemDateResult.systemDate : undefined;

  // Load trading entities for the dropdown
  useEffect(() => {
    const loadEntities = async () => {
      if (!clientId) return;
      try {
        const response = await clientApiService.getTradingEntities(clientId);
        if (response.success && response.data) {
          setEntities(response.data);
        }
      } catch (error) {
        logger.error('Failed to load trading entities:', error);
      }
    };
    loadEntities();
  }, [clientId]);

  // Load all trades (including internal)
  const loadTrades = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const data = await clientService.getCommodityTrades(clientId, undefined, undefined, true);
      setTrades(data);
      logger.info(`Loaded ${data.length} commodity trades (incl. internal)`);
    } catch (err) {
      logger.error('Error loading commodity trades:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  const handleEntityChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedEntityId(e.target.value);
    setSelectedTrades([]);
  }, []);

  const handleMultiSelectionChanged = useCallback((selected: any[]) => {
    setSelectedTrades(selected);
  }, []);

  // Filter trades by entity (client-side)
  const filteredTrades = useMemo(() => {
    if (selectedEntityId === 'all') return trades;
    return trades.filter(t => t.OurPartyId === selectedEntityId);
  }, [trades, selectedEntityId]);

  const formatPendingAllocations = (arr: any): string => {
    if (!Array.isArray(arr) || arr.length === 0) return '';
    return arr.map((a: any) => `${a.entityName} (${a.percentage}%)`).join(', ');
  };

  // CSV export
  const handleExportCsv = useCallback(() => {
    if (selectedTrades.length === 0) return;

    const headers = [
      'Trade #', 'Mirror ID', 'Parent Trade #', 'Status', 'Internal',
      'Legacy Ref 1', 'Our Party', 'Counterparty', 'Product',
      'Underlying', 'Direction', 'Trade Date',
      'Lots', 'Price', 'Quantity', 'Currency', 'Unit',
      'Maturity Date', 'Execution Type',
      'Venue', 'Exchange Contract', 'Contract Month', 'Fixing Ref',
      'Option Type', 'Premium',
      'Underlying Code', 'Venue Code', 'Exchange Contract Code',
      'Settlement Amount', 'Settlement CCY',
      'Value Date', 'Payment Date',
      'Legacy Ref 2', 'Source System', 'Source Channel',
      'Grouping 1', 'Grouping 2', 'Grouping 3', 'Hedge Object',
      'Notes', 'Pending Allocations',
    ];

    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };

    const rows = selectedTrades.map(t => [
      t.TradeNumber, t.MirrorId, t.internalTradeRef?.parentTradeNumber || '',
      t.workflowState, t.isInternalTrade ? 'Yes' : 'No',
      t.LegacyReference1, t.OurParty, t.CounterpartyName, t.ProductType,
      t.UnderlyingName, t.Direction, fmtDate(t.TradeDate),
      t.Lots, t.Price, t.Quantity, t.Currency, t.UnitOfMeasure,
      fmtDate(t.MaturityDate), t.ExecutionType,
      t.VenueName, t.ExchangeContractName, t.ContractMonth, t.FixingReference,
      t.OptionType, t.Premium,
      t.UnderlyingCode, t.VenueCode, t.ExchangeContractCode,
      t.SettlementAmount, t.SettlementCurrency,
      fmtDate(t.ValueDate), fmtDate(t.PaymentDate),
      t.LegacyReference2, t.SourceSystem, t.SourceChannel,
      t.Grouping1, t.Grouping2, t.Grouping3, t.HedgeObject,
      t.Notes, formatPendingAllocations(t.pendingAllocations),
    ].map(escape).join(','));

    const csv = [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commodity_positions_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedTrades]);

  // Excel export
  const handleExportExcel = useCallback(async () => {
    if (selectedTrades.length === 0) return;

    const headers = [
      'Trade #', 'Mirror ID', 'Parent Trade #', 'Status', 'Internal',
      'Legacy Ref 1', 'Our Party', 'Counterparty', 'Product',
      'Underlying', 'Direction', 'Trade Date',
      'Lots', 'Price', 'Quantity', 'Currency', 'Unit',
      'Maturity Date', 'Execution Type',
      'Venue', 'Exchange Contract', 'Contract Month', 'Fixing Ref',
      'Option Type', 'Premium',
      'Underlying Code', 'Venue Code', 'Exchange Contract Code',
      'Settlement Amount', 'Settlement CCY',
      'Value Date', 'Payment Date',
      'Legacy Ref 2', 'Source System', 'Source Channel',
      'Grouping 1', 'Grouping 2', 'Grouping 3', 'Hedge Object',
      'Notes', 'Pending Allocations',
    ];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Commodity Positions');

    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF2D2D2D' },
      };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });

    for (const trade of selectedTrades) {
      worksheet.addRow([
        trade.TradeNumber, trade.MirrorId, trade.internalTradeRef?.parentTradeNumber || '',
        trade.workflowState, trade.isInternalTrade ? 'Yes' : 'No',
        trade.LegacyReference1, trade.OurParty, trade.CounterpartyName,
        trade.ProductType, trade.UnderlyingName, trade.Direction,
        parseDate(trade.TradeDate),
        trade.Lots != null ? Number(trade.Lots) : null,
        trade.Price != null ? Number(trade.Price) : null,
        trade.Quantity != null ? Number(trade.Quantity) : null,
        trade.Currency, trade.UnitOfMeasure,
        parseDate(trade.MaturityDate), trade.ExecutionType,
        trade.VenueName, trade.ExchangeContractName, trade.ContractMonth, trade.FixingReference,
        trade.OptionType,
        trade.Premium != null ? Number(trade.Premium) : null,
        trade.UnderlyingCode, trade.VenueCode, trade.ExchangeContractCode,
        trade.SettlementAmount != null ? Number(trade.SettlementAmount) : null,
        trade.SettlementCurrency,
        parseDate(trade.ValueDate), parseDate(trade.PaymentDate),
        trade.LegacyReference2, trade.SourceSystem, trade.SourceChannel,
        trade.Grouping1, trade.Grouping2, trade.Grouping3, trade.HedgeObject,
        trade.Notes, formatPendingAllocations(trade.pendingAllocations),
      ]);
    }

    // Date formats
    const dateFmt = 'DD/MM/YYYY';
    worksheet.getColumn(12).numFmt = dateFmt;   // Trade Date
    worksheet.getColumn(18).numFmt = dateFmt;   // Maturity Date
    worksheet.getColumn(29).numFmt = dateFmt;   // Value Date
    worksheet.getColumn(30).numFmt = dateFmt;   // Payment Date

    // Number formats
    const numFmt0 = '#,##0';
    const numFmt2 = '#,##0.00';
    const numFmt4 = '#,##0.0000';
    worksheet.getColumn(13).numFmt = numFmt0;   // Lots
    worksheet.getColumn(14).numFmt = numFmt4;   // Price
    worksheet.getColumn(15).numFmt = numFmt4;   // Quantity
    worksheet.getColumn(24).numFmt = numFmt4;   // Premium
    worksheet.getColumn(28).numFmt = numFmt2;   // Settlement Amount

    worksheet.columns.forEach((col) => {
      const header = col.header as string || '';
      col.width = Math.max(header.length + 2, 12);
    });

    worksheet.views = [{ state: 'frozen', ySplit: 1, xSplit: 0 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commodity_positions_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedTrades]);

  // Waterfall chart: bars by Lots, signed by direction
  const tradeBars: TradeBar[] = useMemo(() => {
    if (selectedTrades.length === 0) return [];

    const bars: TradeBar[] = [];
    for (const trade of selectedTrades) {
      const lots = parseFloat(trade.Lots) || 0;
      if (lots === 0) continue;
      const direction = (trade.Direction || '').toLowerCase();
      const signed = (direction === 'buy' || direction === 'compra') ? lots : -lots;
      const tradeNum = trade.TradeNumber || '?';
      bars.push({
        label: tradeNum,
        value: signed,
        underlying: trade.UnderlyingName || '',
        direction: trade.Direction || '',
        isTotal: false,
      });
    }

    bars.sort((a, b) => a.underlying.localeCompare(b.underlying) || a.label.localeCompare(b.label));

    const total = bars.reduce((s, b) => s + b.value, 0);
    bars.push({ label: 'Total', value: total, underlying: '', direction: '', isTotal: true });

    return bars;
  }, [selectedTrades]);

  const chartOption = useMemo(() => {
    if (tradeBars.length === 0) return null;

    const labels = tradeBars.map(b => b.label);
    const seriesData: [number, number, number][] = [];
    let running = 0;

    for (let i = 0; i < tradeBars.length; i++) {
      const bar = tradeBars[i];
      if (bar.isTotal) {
        const lo = Math.min(0, bar.value);
        const hi = Math.max(0, bar.value);
        seriesData.push([i, lo, hi]);
      } else {
        const base = running;
        running += bar.value;
        seriesData.push([i, Math.min(base, running), Math.max(base, running)]);
      }
    }

    const barColors = tradeBars.map(b =>
      b.isTotal ? '#2196F3' : b.value >= 0 ? '#4CAF50' : '#ff6b6b'
    );

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#1e1e1e',
        borderColor: '#444',
        textStyle: { color: '#e0e0e0', fontSize: 13 },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex ?? 0;
          const bar = tradeBars[idx];
          const sign = bar.value >= 0 ? '+' : '';
          if (bar.isTotal) {
            return `<b>Total</b><br/>Net Lots: ${sign}${formatAmount(bar.value)}`;
          }
          return `<b>${bar.label}</b><br/>${bar.underlying} ${bar.direction}<br/>${sign}${formatAmount(bar.value)} lots`;
        }
      },
      grid: { top: 40, right: 20, bottom: 60, left: 80, containLabel: false },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: { color: '#b3b3b3', fontSize: 10, rotate: tradeBars.length > 8 ? 45 : 0 },
        axisLine: { lineStyle: { color: '#444' } },
        axisTick: { lineStyle: { color: '#444' } },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#b3b3b3', formatter: (v: number) => formatAmount(v) },
        axisLine: { lineStyle: { color: '#444' } },
        splitLine: { lineStyle: { color: '#333' } },
      },
      series: [{
        type: 'custom',
        renderItem: (params: any, api: any) => {
          const idx = params.dataIndex;
          const catIdx = api.value(0);
          const lo = api.value(1);
          const hi = api.value(2);
          const loPixel = api.coord([catIdx, lo]);
          const hiPixel = api.coord([catIdx, hi]);
          const bandWidth = api.size([1, 0])[0];
          const barWidth = Math.min(bandWidth * 0.6, 40);
          return {
            type: 'rect',
            shape: {
              x: loPixel[0] - barWidth / 2,
              y: hiPixel[1],
              width: barWidth,
              height: loPixel[1] - hiPixel[1],
            },
            style: { fill: barColors[idx] },
          };
        },
        encode: { x: 0, y: [1, 2] },
        data: seriesData,
        label: {
          show: tradeBars.length <= 12,
          position: 'top',
          color: '#e0e0e0',
          fontSize: 10,
          formatter: (p: any) => {
            const bar = tradeBars[p.dataIndex];
            const sign = bar.value >= 0 ? '+' : '';
            return `${sign}${formatAmount(bar.value)}`;
          },
        },
      }],
    };
  }, [tradeBars]);

  return (
    <div className="positions-container">
      <div className="positions-header">
        <h1>{t('commodities.positions.title')}</h1>
      </div>

      <div className="positions-filters">
        <div className="filter-group">
          <label>{t('positions.entity', 'Entity')}</label>
          <select
            className="positions-select"
            value={selectedEntityId}
            onChange={handleEntityChange}
          >
            <option value="all">{t('positions.allEntities', 'All Entities')}</option>
            {entities.map(entity => (
              <option key={entity.id} value={entity.id}>
                {entity.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>{t('positions.tradeSearch', 'Trade #')}</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              className="positions-select"
              value={tradeNumberSearch}
              onChange={(e) => setTradeNumberSearch(e.target.value)}
              placeholder={t('positions.tradeSearchPlaceholder', 'Filter by trade number...')}
              style={{ width: '180px' }}
            />
            <button
              className={`positions-select-all-btn${filterActiveOnly ? ' positions-filter-active' : ''}`}
              onClick={() => setFilterActiveOnly(prev => !prev)}
              title={t('positions.filterActiveTooltip')}
            >
              {t('positions.filterActive')}
            </button>
          </div>
        </div>
        <button
          className="positions-select-all-btn"
          onClick={() => setSelectAllTrigger(n => n + 1)}
        >
          {t('positions.selectAll')}
        </button>
        <button
          className="positions-select-all-btn"
          onClick={() => setClearAllTrigger(n => n + 1)}
        >
          {t('positions.clearAll')}
        </button>
        <button
          className="positions-select-all-btn positions-export-btn"
          onClick={handleExportCsv}
          disabled={selectedTrades.length === 0}
          title={selectedTrades.length === 0 ? t('positions.exportCsvHint') : undefined}
        >
          {t('positions.exportCsv')}
        </button>
        <button
          className="positions-select-all-btn positions-export-btn"
          onClick={handleExportExcel}
          disabled={selectedTrades.length === 0}
          title={selectedTrades.length === 0 ? t('positions.exportExcelHint') : undefined}
        >
          {t('positions.exportExcel')}
        </button>
      </div>

      <div className={`positions-content ${chartExpanded ? '' : 'chart-collapsed'}`}>
        <div className="positions-grid-wrapper">
          <CommodityTradesGrid
            trades={filteredTrades}
            loading={loading}
            multiSelect={true}
            onMultiSelectionChanged={handleMultiSelectionChanged}
            selectAllTrigger={selectAllTrigger}
            clearAllTrigger={clearAllTrigger}
            tradeNumberSearch={tradeNumberSearch || undefined}
            maturityDateFrom={maturityDateFrom}
            showMirrorId={true}
            showParentTradeNumber={true}
            suppressDefaultSort={false}
          />
        </div>

        <button
          className="chart-toggle-btn"
          onClick={() => setChartExpanded(!chartExpanded)}
          title={chartExpanded ? t('positions.hideChart', 'Hide chart') : t('positions.showChart', 'Show chart')}
        >
          {chartExpanded ? <ChevronRight size={16} /> : <BarChart3 size={14} />}
          {!chartExpanded && <ChevronLeft size={14} />}
        </button>

        {chartExpanded && (
          <div className="positions-charts-panel">
            <div className="chart-section">
              <h3>{t('positions.netPosition')}</h3>
              {selectedTrades.length === 0 ? (
                <div className="chart-empty-state">
                  {t('positions.selectTrades')}
                </div>
              ) : tradeBars.length === 0 ? (
                <div className="chart-empty-state">
                  {t('positions.noPositionData')}
                </div>
              ) : (
                <ReactEcharts
                  option={chartOption}
                  style={{ height: '100%', width: '100%' }}
                  opts={{ renderer: 'canvas' }}
                />
              )}
            </div>
            {selectedTrades.length > 0 && (
              <div className="positions-summary">
                <span className="summary-label">{t('positions.tradesSelected')}:</span>
                <span className="summary-value">{selectedTrades.length}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommodityPositions;
